import { useCallback, useState } from 'react';

import {
  ButtonWithPopover,
  ButtonWithPopoverProps,
} from '@/components/form/baseInputs/Button/ButtonWithPopover';
import { PlusIcon } from '@/components/icons/PlusIcon';
import { MenuItem } from '@/components/poppers/MenuPopper/MenuItem';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { LogNewGiftModal } from '@/modules/gifting/LogNewGiftModal/LogNewGiftModal';
import { RecordExemptionUsedModal } from '@/modules/gifting/RecordExemptionUsedModal/RecordExemptionUsedModal';
import { useHouseholdDetailsContext } from '@/modules/household/contexts/householdDetails.context';
import { UnreachableError } from '@/utils/errors';

export type ClientGiftingAddButtonProps = Pick<
  ButtonWithPopoverProps,
  'popperProps' | 'popperVariant'
>;

export function ClientGiftingAddButton(props: ClientGiftingAddButtonProps) {
  const { householdId } = useHouseholdDetailsContext();
  const canEdit = useCanEditHouseholdData();

  const [isAddGiftModalOpen, setIsAddGiftModalOpen] = useState(false);
  const [isAddExemptionModalOpen, setIsAddExemptionModalOpen] = useState(false);
  const handleMenuClick = useCallback((kind: 'gift' | 'exemption') => {
    // make sure we close all modals before opening a new one; this *should* never happen,
    // but just in case
    setIsAddGiftModalOpen(false);
    setIsAddExemptionModalOpen(false);

    switch (kind) {
      case 'gift':
        setIsAddGiftModalOpen(true);
        break;
      case 'exemption':
        setIsAddExemptionModalOpen(true);
        break;
      default:
        throw new UnreachableError({
          case: kind,
          message: `Unreachable case in ClientGiftingAddButton: ${kind}`,
        });
    }
  }, []);

  if (!householdId || !canEdit) return null;

  return (
    <>
      {/* always unmount/remount to clear state */}

      <LogNewGiftModal
        householdId={householdId}
        isOpen={isAddGiftModalOpen}
        onClose={() => setIsAddGiftModalOpen(false)}
      />
      <RecordExemptionUsedModal
        householdId={householdId}
        isOpen={isAddExemptionModalOpen}
        onClose={() => setIsAddExemptionModalOpen(false)}
      />
      <ButtonWithPopover
        label="Add"
        startIcon={PlusIcon}
        size="sm"
        variant="primary"
        data-testid="gifting-page-add-button"
        {...props}
      >
        <MenuItem label="Log gift" onClick={() => handleMenuClick('gift')} />
        <MenuItem
          label="Record exemption"
          onClick={() => handleMenuClick('exemption')}
        />
      </ButtonWithPopover>
    </>
  );
}
